import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./use-auth";

interface WebSocketMessage {
  type: 'performer_created' | 'performer_updated' | 'performer_deleted' | 'performers' | 'message_created' | 'participant_created' | 'participant_updated' | 'event_updated' | 'event_created' | 'event_deleted' | 'jam_night_updated' | 'jam_night_created' | 'jam_night_deleted' | 'theme_updated' | 'raffle_winner' | 'raffle_reset' | 'user_settings_updated' | 'force_logout' | 'organization_membership_changed' | 'act:created' | 'act:updated' | 'act:deleted' | 'external-event:created' | 'external-event:updated' | 'external-event:deleted' | 'booking:created' | 'booking:updated' | 'booking:cancelled';
  jamNightId?: number;
  eventId?: number;
  organisationId?: number;
  performer?: any;
  performerId?: number;
  message?: any;
  participant?: any;
  jamNight?: any;
  event?: any;
  act?: any;
  actId?: number;
  booking?: any;
  bookingId?: number;
  updatedData?: any;
  completed?: boolean;
  signupEnabled?: boolean;
  signupLimit?: number | null;
  signupLimitReached?: boolean;
  feedbackEnabled?: boolean;
  feedbackUrl?: string;
  raffleEnabled?: boolean;
  logoUrl?: string;
  userId?: number;
  theme?: string;
  action?: string;
  winnerId?: number;
  winnerName?: string;
  settings?: { multiScreenJams?: boolean };
  organizationId?: number;
}

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    try {
      // Get the WebSocket URL based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected for real-time updates');
        reconnectAttemptsRef.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          
          // Debug logging for WebSocket messages
          console.log('[WebSocket] Received message:', data.type, {
            eventId: data.eventId,
            jamNightId: data.jamNightId,
            organisationId: data.organisationId,
            bookingId: data.bookingId,
            booking: data.booking
          });
          
          // Invalidate relevant queries based on message type
          switch (data.type) {
            case 'performer_created':
            case 'performer_updated':
            case 'performer_deleted':
            case 'performers':
            case 'raffle_winner':
            case 'raffle_reset':
              // Refresh performers for the specific jam night
              if (data.jamNightId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/performers?jamNightId=${data.jamNightId}`]
                });
              }
              // Also refresh general performers query
              queryClient.invalidateQueries({
                queryKey: ['/api/performers']
              });
              // Refresh jam nights list to update performer counts
              queryClient.invalidateQueries({
                queryKey: ["jamNights"]
              });
              break;
              
            case 'participant_created':
            case 'participant_updated':
              // Refresh participants for the specific organisation
              if (data.organisationId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/participants?organisationId=${data.organisationId}`]
                });
              }
              
              // For participant updates, also refresh all performer queries
              // since performers display participant data (name, email, phone, instagram)
              if (data.type === 'participant_updated') {
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && queryKey.startsWith('/api/performers');
                  }
                });
              }
              break;
              
            case 'message_created':
              // Refresh messages for the specific jam night
              if (data.jamNightId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/messages?jamNightId=${data.jamNightId}`]
                });
              }
              break;

            case 'event_updated':
            case 'event_created':
            case 'event_deleted':
            case 'jam_night_updated':
            case 'jam_night_created':
            case 'jam_night_deleted':
              // Refresh events list for home page
              queryClient.invalidateQueries({
                queryKey: ["events"]
              });
              // Also invalidate the legacy jamNights key in case it's used somewhere
              queryClient.invalidateQueries({
                queryKey: ["jamNights"]
              });
              // Refresh specific event details - check both eventId and jamNightId
              const eventId = data.eventId || data.jamNightId;
              if (eventId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/events/${eventId}`]
                });
                // Also invalidate queries that might include this event data
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && 
                           (queryKey.includes(`/api/events/${eventId}`) ||
                            queryKey.includes(`jamNightId=${eventId}`) ||
                            queryKey.includes(`eventId=${eventId}`));
                  }
                });
              }
              break;

            case 'theme_updated':
              // Only handle theme updates for the current user
              if (data.userId === user?.id && data.theme) {
                // Update the user data in localStorage
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                  try {
                    const userData = JSON.parse(storedUser);
                    userData.theme = data.theme;
                    localStorage.setItem('user', JSON.stringify(userData));
                  } catch (error) {
                    console.error('Error updating user theme in localStorage:', error);
                  }
                }
                
                // Force page reload to apply theme change immediately
                window.location.reload();
              }
              break;

            case 'user_settings_updated':
              if (data.userId === user?.id && data.settings) {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                  try {
                    const userData = JSON.parse(storedUser);
                    const updatedUser = { ...userData, ...data.settings };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    window.dispatchEvent(new Event('user-settings-changed'));
                  } catch (error) {
                    console.error('Error updating user settings in localStorage:', error);
                  }
                }
              }
              break;
              
            case 'force_logout':
              // Check if this force logout is for the current user
              if (data.userId === user?.id) {
                console.log('Received force logout event - logging out user');
                
                // Clear local storage
                localStorage.removeItem('user');
                
                // Close WebSocket connection cleanly
                if (wsRef.current) {
                  wsRef.current.close(1000, 'Force logout');
                }
                
                // Redirect to login page
                window.location.href = '/login';
              }
              break;
              
            case 'act:created':
            case 'act:updated':
            case 'act:deleted':
              // Refresh acts for the specific organisation
              if (data.organisationId) {
                queryClient.invalidateQueries({
                  queryKey: [`/api/acts?organisationId=${data.organisationId}`]
                });
                queryClient.invalidateQueries({
                  queryKey: [`/api/acts`]
                });
                // Refresh specific act if actId is provided
                if (data.actId || data.act?.id) {
                  const actId = data.actId || data.act.id;
                  queryClient.invalidateQueries({
                    queryKey: [`/api/acts/${actId}`]
                  });
                }
              }
              break;

            case 'external-event:created':
            case 'external-event:updated':
            case 'external-event:deleted':
              console.log('[WebSocket] External event update:', data.type, {
                organisationId: data.organisationId,
                eventId: data.eventId || data.event?.id,
                event: data.event
              });
              // Refresh external events for the specific organisation
              if (data.organisationId) {
                // Invalidate all external events queries (with and without org ID)
                queryClient.invalidateQueries({
                  queryKey: [`/api/external-events`]
                });
                queryClient.invalidateQueries({
                  queryKey: [`/api/external-events?organisationId=${data.organisationId}`]
                });
                // Also invalidate using predicate to catch all variations
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && queryKey.startsWith('/api/external-events');
                  }
                });
                // Refresh specific event if eventId is provided
                if (data.eventId || data.event?.id) {
                  const eventId = data.eventId || data.event.id;
                  queryClient.invalidateQueries({
                    queryKey: [`/api/external-events/${eventId}`]
                  });
                }
              }
              break;

            case 'booking:created':
            case 'booking:updated':
            case 'booking:cancelled':
              console.log('[WebSocket] Booking update received:', {
                type: data.type,
                organisationId: data.organisationId,
                bookingId: data.bookingId || data.booking?.id,
                booking: data.booking
              });
              
              // Refresh bookings for the specific organisation
              if (data.organisationId) {
                console.log('[WebSocket] Invalidating booking queries for org:', data.organisationId);
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    const queryKey = query.queryKey[0];
                    return typeof queryKey === 'string' && queryKey.startsWith('/api/bookings');
                  }
                });
                // Also refresh acts and events since bookings affect their statistics
                queryClient.invalidateQueries({
                  queryKey: [`/api/acts`]
                });
                queryClient.invalidateQueries({
                  queryKey: [`/api/external-events`]
                });
              }
              
              // Refresh specific booking if bookingId is provided
              if (data.bookingId || data.booking?.id) {
                const bookingId = data.bookingId || data.booking.id;
                console.log('[WebSocket] Invalidating specific booking:', bookingId);
                queryClient.invalidateQueries({
                  queryKey: [`/api/bookings/${bookingId}`]
                });
                // Force a refetch to ensure the data updates
                queryClient.refetchQueries({
                  queryKey: [`/api/bookings/${bookingId}`]
                });
              }
              break;

            case 'organization_membership_changed':
              // Organization membership changed - refresh organization and user data
              console.log('Organization membership changed - refreshing data');
              queryClient.invalidateQueries({ queryKey: ["organisations"] });
              queryClient.invalidateQueries({ queryKey: ["users"] });
              
              // If this affects the current user, refresh their auth data
              if (data.userId === user?.id) {
                console.log('Current user organization membership changed - refreshing auth data');
                
                // Check if user was removed from organization and redirect if needed
                const isRemoved = data.action === 'removed';
                const removedOrgId = data.organizationId;
                
                // Fetch fresh user data from server
                fetch('/api/auth/user', {
                  method: 'GET',
                  credentials: 'include',
                })
                .then(response => {
                  if (response.ok) {
                    return response.json();
                  }
                  throw new Error('Failed to fetch user data');
                })
                .then(userData => {
                  // Update localStorage with fresh user data
                  localStorage.setItem('user', JSON.stringify(userData));
                  
                  // Trigger a custom event to update the auth context
                  window.dispatchEvent(new CustomEvent('user-auth-changed', { detail: userData }));
                  
                  // If user was removed from organization, check if they need to be redirected
                  if (isRemoved && removedOrgId) {
                    const currentPath = window.location.pathname;
                    console.log('User removed from organization:', removedOrgId, 'current path:', currentPath);
                    
                    // Check if current page is an event from the organization they were removed from
                    const eventMatch = currentPath.match(/\/event\/(\d+)/);
                    console.log('Event match:', eventMatch);
                    if (eventMatch) {
                      const eventId = parseInt(eventMatch[1]);
                      console.log('Checking event ID:', eventId, 'against removed org ID:', removedOrgId);
                      
                      // Fetch the event to check its organization
                      fetch(`/api/events/${eventId}`, {
                        method: 'GET',
                        credentials: 'include',
                      })
                      .then(response => {
                        if (response.ok) {
                          return response.json();
                        }
                        // If we can't fetch the event, assume user no longer has access
                        throw new Error('Event not accessible');
                      })
                      .then(event => {
                        console.log('Event data:', event);
                        // Check if this event belongs to the organization they were removed from
                        if (event && event.organisationId === removedOrgId) {
                          console.log('Event belongs to removed organization - redirecting to home page');
                          window.location.href = '/';
                        } else {
                          console.log('Event does not belong to removed organization, no redirect needed');
                        }
                      })
                      .catch(error => {
                        console.log('Event not accessible after organization removal - redirecting to home page');
                        window.location.href = '/';
                      });
                      return; // Exit early to avoid checking other page types
                    }
                    
                    // Check if current page is an analytics page from the organization they were removed from
                    const analyticsMatch = currentPath.match(/\/analytics\/(\d+)/);
                    console.log('Analytics match:', analyticsMatch);
                    if (analyticsMatch) {
                      const analyticsOrgId = parseInt(analyticsMatch[1]);
                      console.log('Checking analytics org ID:', analyticsOrgId, 'against removed org ID:', removedOrgId);
                      
                      // Check if this analytics page belongs to the organization they were removed from
                      if (analyticsOrgId === removedOrgId) {
                        console.log('Analytics page belongs to removed organization - redirecting to home page');
                        window.location.href = '/';
                      } else {
                        console.log('Analytics page does not belong to removed organization, no redirect needed');
                      }
                      return; // Exit early to avoid checking other page types
                    }
                    
                    // Check if current page is a participant detail from the organization they were removed from
                    const participantMatch = currentPath.match(/^\/participants\/(\d+)/);
                    if (participantMatch) {
                      // Since there's no single participant endpoint, we'll check if user still has access
                      // to any organization. If they were removed from their last organization and are viewing
                      // a participant page, redirect them to home
                      const userOrganizations = userData.organisationUsers || [];
                      const userHasOrganizations = userOrganizations.length > 0;
                      
                      if (!userHasOrganizations) {
                        console.log('User has no organizations - redirecting to home page');
                        window.location.href = '/';
                      }
                    }
                  }
                  
                  // Also invalidate home page data to refresh organization groups
                  queryClient.invalidateQueries({ queryKey: ["events"] });
                  queryClient.invalidateQueries({ queryKey: ["jamNights"] });
                })
                .catch(error => {
                  console.error('Error refreshing user data:', error);
                });
              }
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', {
          code: event.code, 
          reason: event.reason,
          wasClean: event.wasClean,
          url: wsRef.current?.url
        });
        
        // Only attempt reconnection if it wasn't a clean close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            console.log(`Attempting WebSocket reconnection (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', {
          error,
          url: wsRef.current?.url,
          readyState: wsRef.current?.readyState
        });
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }, [queryClient, user?.id]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    reconnect: connect
  };
}